import React from 'react';
import { ChevronDown, ShieldCheck, Star, User } from 'lucide-react';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';

import { Card, CardContent } from '../../../components/ui/card';
import SellerBadges, { getUserMembership } from '../../../components/SellerBadges/SellerBadges';

import { getAvatarUrl } from './profileAvatar';
import { REVIEWS_SECTION_ANCHOR_ID } from './ProfileReviewsSection';

type ProfileHeaderCardProps = {
  user: any;
  displayName: string | null;
  bio: string | null;
};

const ProfileHeaderCard = ({ user, displayName, bio }: ProfileHeaderCardProps) => {
  const intl = useIntl();

  const metadata = user?.attributes?.profile?.metadata || {};
  const reviewAvg: number | null = metadata.reviewAvg ?? null;
  const reviewCount: number = metadata.reviewCount ?? 0;
  const { tier: membershipTier, isVerified } = getUserMembership(user);

  const createdAt = user?.attributes?.createdAt;
  const joinedDate = createdAt
    ? intl.formatDate(new Date(createdAt), { month: 'short', year: 'numeric' })
    : null;

  const avatarUrl = getAvatarUrl(user);

  const scrollToReviews = () => {
    if (typeof document === 'undefined') return;
    document
      .getElementById(REVIEWS_SECTION_ANCHOR_ID)
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <Card className="border-border/80 bg-white shadow-[0_16px_40px_rgba(23,21,19,0.06)]">
      <CardContent className="p-5 md:p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex min-w-0 flex-col gap-4 md:flex-row md:items-start">
            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt={displayName || ''}
                className="h-20 w-20 shrink-0 rounded-full border-2 border-[#f1e7d7] object-cover shadow-sm md:h-24 md:w-24"
                data-testid="img-public-profile-avatar"
              />
            ) : (
              <div
                className="flex h-20 w-20 shrink-0 items-center justify-center rounded-full border-2 border-[#f1e7d7] bg-primary/10 shadow-sm md:h-24 md:w-24"
                data-testid="img-public-profile-avatar"
              >
                <User className="h-10 w-10 text-primary md:h-12 md:w-12" />
              </div>
            )}

            <div className="min-w-0 space-y-3">
              <SellerBadges membershipTier={membershipTier} isVerified={isVerified} />


              <div className="space-y-2">
                <h1
                  className="text-3xl font-heading font-bold uppercase tracking-tight md:text-5xl"
                  data-testid="text-public-profile-name"
                >
                  {displayName || ''}
                </h1>
                {bio ? (
                  <p
                    className="max-w-3xl text-sm leading-6 text-muted-foreground md:text-base"
                    data-testid="text-public-profile-bio"
                  >
                    {bio}
                  </p>
                ) : null}
              </div>

              <div className="flex flex-wrap gap-2 text-xs font-medium text-muted-foreground md:text-sm">
                {reviewAvg != null ? (
                  <button
                    type="button"
                    onClick={scrollToReviews}
                    title={intl.formatMessage({ id: 'CustomProfilePage.ratingPillHint' })}
                    aria-label={intl.formatMessage({ id: 'CustomProfilePage.ratingPillHint' })}
                    className="group inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-border bg-[#faf7f1] px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:border-primary hover:bg-primary/10 hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary md:text-sm"
                    data-testid="text-public-profile-rating"
                  >
                    <Star className="h-4 w-4 fill-primary text-primary" />
                    <span>
                      <FormattedMessage
                        id="CustomProfilePage.ratingPill"
                        values={{
                          rating: Number(reviewAvg).toFixed(1),
                          count: reviewCount,
                        }}
                      />
                    </span>
                    <ChevronDown className="h-3.5 w-3.5 text-primary transition-transform group-hover:translate-y-0.5" />
                  </button>
                ) : null}
                {joinedDate ? (
                  <span
                    className="inline-flex items-center gap-1.5 rounded-full border border-border bg-[#faf7f1] px-3 py-1.5"
                    data-testid="text-public-profile-joined"
                  >
                    <ShieldCheck className="h-4 w-4 text-primary" />
                    <FormattedMessage
                      id="CustomProfilePage.joinedPill"
                      values={{ date: joinedDate }}
                    />
                  </span>
                ) : null}
              </div>
            </div>
          </div>

        </div>
      </CardContent>
    </Card>
  );
};

export default ProfileHeaderCard;
